import React, { FC } from 'react';
import { clampPercent } from '../../utility/listUploadProgress';
import { useSawInProgress } from '../../hooks/useSawInProgress';

type ListUploadStatusBarProps = {
  status: string;
  progressPercent?: number | null;
  onRefresh?: () => void;
}

const STEPS = [
  'Queued for parsing',
  'Parsing CSV',
  'Updating property data',
  'Updating estimated values'
];

const reloadPage = () => window.location.reload();

const ListUploadStatusBar: FC<ListUploadStatusBarProps> = ({ status, progressPercent, onRefresh = reloadPage }) => {
  const sawInProgress = useSawInProgress(status);
  const isError = status === 'Error';
  const isComplete = status === 'Complete';
  const stepIndex = STEPS.indexOf(status);

  if (stepIndex === -1 && !isError && !isComplete) return null;

  const percent = isComplete ? 100 : clampPercent(progressPercent);
  const showRefresh = isComplete && sawInProgress;

  if (showRefresh) {
    return (
      <div className='w-full'>
        <button
          type='button'
          onClick={onRefresh}
          className='w-full rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600'
        >
          100% complete -- Refresh to see your data
        </button>
      </div>
    );
  }

  const fillClass = isError ? 'bg-red-400' : isComplete ? 'bg-green-500' : 'bg-indigo-500';

  return (
    <div className='w-full'>
      <div className='flex items-center justify-between mb-2'>
        <span className={`text-sm font-medium ${isError ? 'text-red-600' : 'text-gray-900'}`}>
          {isError ? 'Error processing list' : status}
          {stepIndex !== -1 && <span className='ml-2 text-xs font-normal text-gray-500'>Step {stepIndex + 1} of {STEPS.length}</span>}
        </span>
        {!isError && percent !== null && (
          <span className='text-sm font-semibold text-gray-900'>{percent}%</span>
        )}
      </div>
      <div
        role='progressbar'
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={isError ? 100 : percent ?? 0}
        className='h-2 w-full rounded-full bg-gray-200 overflow-hidden'
      >
        <div
          className={`h-2 rounded-full transition-all duration-500 ${fillClass}`}
          style={{ width: `${isError ? 100 : percent ?? 0}%` }}
        />
      </div>
      {!isError && !isComplete && (
        <p className='mt-2 text-xs text-gray-400'>This can take a few minutes -- feel free to leave this page</p>
      )}
    </div>
  );
};

export default ListUploadStatusBar;
